// Reference overlays (OVL-1..3, E-40). CLIENT render chrome ONLY — a user image floats
// as a horizontal ghost plane you align to the world, exactly like the minimap: the sim
// never reads it, never hashes it, never replays it (OVL-2/3). Blobs live in the
// `overlays` IDB store; the small placements live in `WorldMeta.overlays[]`. Earth-only
// in v1. A missing blob at load is dropped with a toast, the world unaffected (E-40).
import * as THREE from 'three';
import { OVL } from '../../sim/config';
import type { OverlayPlacement, WorldMeta } from '../storage/saves';

/** Ground width/height of an overlay plane in blocks from image pixels × a ground
 *  resolution (m/px) ÷ the world scale (m/block). Pure (client-testable). */
export function overlaySizeBlocks(pxW: number, pxH: number, mPerPx: number, scaleM: number): { widthBlocks: number; heightBlocks: number } {
  const widthBlocks = Math.max(1, (pxW * mPerPx) / scaleM);
  const heightBlocks = pxW > 0 ? widthBlocks * (pxH / pxW) : widthBlocks;
  return { widthBlocks, heightBlocks };
}

/** Bytes encoded by a `data:` URL's base64 payload (E-40 size gate). */
export function dataUrlBytes(dataUrl: string): number {
  const i = dataUrl.indexOf(',');
  const b64 = i >= 0 ? dataUrl.slice(i + 1) : dataUrl;
  const pad = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((b64.length * 3) / 4) - pad);
}

/** True for a supported image data URL (PNG/JPEG only, E-40). */
export function isSupportedImage(dataUrl: string): boolean {
  return /^data:image\/(png|jpeg|jpg);base64,/.test(dataUrl);
}

interface Quad { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial; geom: THREE.PlaneGeometry; tex: THREE.Texture; }

/** The render side the controller drives (real = OverlayRenderer; tests inject a stub
 *  so the controller logic runs without a DOM/WebGL TextureLoader). */
export interface IOverlayRenderer {
  set(p: OverlayPlacement, dataUrl: string): void;
  applyPlacement(p: OverlayPlacement): void;
  remove(id: string): void;
  count(): number;
  dispose(): void;
}

/** The THREE side: one horizontal textured quad per placement, anchored at its
 *  NW/top-left corner (the image extends +x east / +z south). */
export class OverlayRenderer implements IOverlayRenderer {
  private readonly group = new THREE.Group();
  private readonly quads = new Map<string, Quad>();
  private readonly loader = new THREE.TextureLoader();

  constructor(scene: THREE.Scene) {
    this.group.frustumCulled = false;
    scene.add(this.group);
  }

  /** Add or replace an overlay from its placement + image data URL. The plane's height
   *  comes from the loaded image aspect; `applyPlacement` positions/opacities it. */
  set(p: OverlayPlacement, dataUrl: string): void {
    this.remove(p.overlayId);
    const tex = this.loader.load(dataUrl, (t) => {
      const img = t.image as { width?: number; height?: number };
      const aspect = img.width && img.height ? img.height / img.width : 1;
      const q = this.quads.get(p.overlayId);
      if (q) { q.geom.dispose(); q.mesh.geometry = q.geom = new THREE.PlaneGeometry(p.widthBlocks, p.widthBlocks * aspect); this.applyPlacement(p); }
    });
    tex.colorSpace = THREE.SRGBColorSpace;
    const geom = new THREE.PlaneGeometry(p.widthBlocks, p.widthBlocks);
    const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: p.opacity, depthWrite: false, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(geom, mat);
    mesh.rotation.x = -Math.PI / 2; // lie flat in the XZ plane, facing up
    mesh.frustumCulled = false;
    this.group.add(mesh);
    this.quads.set(p.overlayId, { mesh, mat, geom, tex });
    this.applyPlacement(p);
  }

  /** Reposition / re-opacity / show-hide from the placement (no texture reload). */
  applyPlacement(p: OverlayPlacement): void {
    const q = this.quads.get(p.overlayId);
    if (!q) return;
    const h = (q.geom.parameters.height ?? p.widthBlocks);
    // Anchor is the NW/top-left corner; the centred plane sits half a span SE of it.
    q.mesh.position.set(p.anchor.x + p.widthBlocks / 2, p.anchor.y, p.anchor.z + h / 2);
    q.mat.opacity = p.opacity;
    q.mesh.visible = !p.hidden;
  }

  remove(id: string): void {
    const q = this.quads.get(id);
    if (!q) return;
    this.group.remove(q.mesh);
    q.geom.dispose(); q.mat.dispose(); q.tex.dispose();
    this.quads.delete(id);
  }

  has(id: string): boolean { return this.quads.has(id); }
  count(): number { return this.quads.size; }

  dispose(): void {
    for (const id of [...this.quads.keys()]) this.remove(id);
    this.group.parent?.remove(this.group);
  }
}

export type OverlayResult = { ok: true; id: string } | { ok: false; reason: string };

export interface OverlayHost {
  scene: THREE.Scene;
  /** The world meta — `meta.overlays` is the source of truth for placements. */
  meta: WorldMeta;
  save: {
    putOverlayBlob(id: string, dataUrl: string): Promise<void>;
    getOverlayBlob(id: string): Promise<string | undefined>;
    deleteOverlayBlob(id: string): Promise<void>;
  };
  /** World metres/block, for `widthBlocks`. */
  scaleM: number;
  /** Where a fresh overlay anchors (the player's position = the image's NW corner). */
  anchorAt: () => { x: number; y: number; z: number };
  /** Persist the (mutated) meta + blobs (saveNow). */
  persist: () => void;
  /** Surface a message to the player (E-40 rejects / missing-blob drops). */
  toast: (head: string, title: string) => void;
  /** Inject a render stub (tests); default = a real `OverlayRenderer` over `scene`. */
  renderer?: IOverlayRenderer;
}

let overlaySeq = 0;

/** Owns overlay state: the renderer + the placement list (`meta.overlays`) + the blob
 *  store. All the panel / debug seam call through here. Never touches the sim. */
export class OverlayController {
  readonly renderer: IOverlayRenderer;
  private readonly host: OverlayHost;

  constructor(host: OverlayHost) {
    this.host = host;
    this.renderer = host.renderer ?? new OverlayRenderer(host.scene);
    if (!host.meta.overlays) host.meta.overlays = [];
  }

  private get placements(): OverlayPlacement[] { return this.host.meta.overlays!; }

  /** Restore every persisted overlay; a placement whose blob is gone is dropped with a
   *  toast and the world is left otherwise untouched (E-40). */
  async loadAll(): Promise<void> {
    const kept: OverlayPlacement[] = [];
    let dropped = 0;
    for (const p of this.placements) {
      const blob = await this.host.save.getOverlayBlob(p.overlayId);
      if (!blob) { dropped++; continue; }
      this.renderer.set(p, blob);
      kept.push(p);
    }
    if (dropped > 0) {
      this.host.meta.overlays = kept;
      this.host.toast('Overlay unavailable', `${dropped} overlay image${dropped > 1 ? 's' : ''} missing — placement dropped`);
      this.host.persist();
    }
  }

  /** Upload an image (PNG/JPEG data URL) at the player's position (NW corner). Rejects
   *  oversized / unsupported / over-cap with a reason (E-40). */
  async add(dataUrl: string, pxW: number, pxH: number): Promise<OverlayResult> {
    if (!isSupportedImage(dataUrl)) return this.reject('Unsupported image (PNG or JPEG only)');
    if (dataUrlBytes(dataUrl) > OVL.MAX_BYTES) return this.reject(`Image too large (max ${Math.round(OVL.MAX_BYTES / 1_048_576)} MiB)`);
    if (this.placements.length >= OVL.MAX_PER_WORLD) return this.reject(`Too many overlays (max ${OVL.MAX_PER_WORLD})`);
    const id = `ovl_${Date.now().toString(36)}_${(overlaySeq++).toString(36)}`;
    const { widthBlocks } = overlaySizeBlocks(pxW, pxH, OVL.DEFAULT_M_PER_PX, this.host.scaleM);
    const a = this.host.anchorAt();
    const p: OverlayPlacement = { overlayId: id, anchor: { x: a.x, y: a.y, z: a.z }, widthBlocks, opacity: OVL.DEFAULT_OPACITY, hidden: false };
    // Register the placement + quad synchronously (client-only; touches zero sim state,
    // OVL-3), THEN persist the blob — so a caller can observe the overlay immediately.
    this.placements.push(p);
    this.renderer.set(p, dataUrl);
    await this.host.save.putOverlayBlob(id, dataUrl);
    this.host.persist();
    return { ok: true, id };
  }

  private reject(reason: string): OverlayResult {
    this.host.toast('Overlay rejected', reason);
    return { ok: false, reason };
  }

  remove(id: string): void {
    const i = this.placements.findIndex((p) => p.overlayId === id);
    if (i < 0) return;
    this.placements.splice(i, 1);
    this.renderer.remove(id);
    void this.host.save.deleteOverlayBlob(id);
    this.host.persist();
  }

  private mutate(id: string, fn: (p: OverlayPlacement) => void): void {
    const p = this.placements.find((q) => q.overlayId === id);
    if (!p) return;
    fn(p);
    this.renderer.applyPlacement(p);
    this.host.persist();
  }

  nudge(id: string, dx: number, dy: number, dz: number): void {
    this.mutate(id, (p) => { p.anchor.x += dx * OVL.MOVE_STEP; p.anchor.y += dy * OVL.MOVE_STEP; p.anchor.z += dz * OVL.MOVE_STEP; });
  }
  setOpacity(id: string, opacity: number): void { this.mutate(id, (p) => { p.opacity = Math.max(0, Math.min(1, opacity)); }); }
  toggleHidden(id: string): void { this.mutate(id, (p) => { p.hidden = !p.hidden; }); }

  list(): OverlayPlacement[] { return [...this.placements]; }
  count(): number { return this.renderer.count(); }

  dispose(): void { this.renderer.dispose(); }
}
